import { ActionIcon, Box, Group, Menu, Text, Tooltip } from '@mantine/core';
import { IconDotsVertical, IconInfoCircle } from '@tabler/icons-react';
import { useState } from 'react';
import { Client } from 'src/admin/users/types';
import { useDeleteClient } from 'src/clients/hooks/useDeleteClient';
import { useFamily } from 'src/clients/hooks/useFamily';
import { useFamilyRoles } from 'src/clients/hooks/useFamilyRoles';
import { useRemoveFamilyMember } from 'src/clients/hooks/useRemoveFamilyMember';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { SortableColumn } from 'src/common/components/table/types';
import { useConfirmationPopup } from 'src/design-system/useConfirmationPopup';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatName, NameFormat } from 'src/utils/nameFormatting';

type Props = {
  familyId: string;
};

export function FamilyMembersTable({ familyId }: Props) {
  const { family } = useFamily(familyId);
  const { familyRoles } = useFamilyRoles();
  const {
    deleteClient,
    error: deleteError,
    isLoading: isDeleting,
    reset: resetDelete,
  } = useDeleteClient();
  const {
    removeFamilyMember,
    error: removeError,
    isLoading: isRemoving,
    reset: resetRemove,
  } = useRemoveFamilyMember();
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [clientToRemove, setClientToRemove] = useState<Client | null>(null);
  const { showConfirmation, confirmationElement } = useConfirmationPopup();

  function handleDeletePress(client: Client) {
    resetDelete();
    setClientToDelete(client);
  }

  async function handleDeleteConfirm() {
    if (!clientToDelete) return;
    try {
      await deleteClient(clientToDelete.id);
      setClientToDelete(null);
      showConfirmation('Client deleted');
    } catch {
      // error surfaced via deleteError
    }
  }

  function handleRemovePress(client: Client) {
    resetRemove();
    setClientToRemove(client);
  }

  async function handleRemoveConfirm() {
    if (!clientToRemove) return;
    const removedName = formatName(
      clientToRemove.firstName,
      clientToRemove.lastName,
      NameFormat.NATURAL,
    );
    try {
      await removeFamilyMember(familyId, clientToRemove.id);
      setClientToRemove(null);
      showConfirmation(`${removedName} removed from a family`);
    } catch {
      // error surfaced via removeError
    }
  }

  const roleNameById = new Map(familyRoles.map(role => [role.id, role.name]));
  const roleIdByClientId = new Map(
    (family?.members ?? []).map(member => [member.clientId, member.familyRoleId]),
  );

  const columnsBase: SortableColumn<Client>[] = [
    {
      accessor: 'name',
      title: 'Member',
      render: (client: Client) => (
        <Box>
          <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
            {formatName(client.firstName, client.lastName, NameFormat.NATURAL)}
          </Text>
          {client.emailAddress && (
            <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.7">
              {client.emailAddress}
            </Text>
          )}
        </Box>
      ),
    },
    {
      accessor: 'role',
      title: 'Role',
      render: (client: Client) => {
        const roleId = roleIdByClientId.get(client.id);
        const roleName = roleId ? roleNameById.get(roleId) : null;
        return (
          <Text size="sm" fw={FONT_WEIGHTS.medium} c={roleName ? 'black' : 'gray.6'}>
            {roleName ?? '—'}
          </Text>
        );
      },
    },
    {
      accessor: 'lastUpdated',
      sortable: false,
      title: (
        <Group gap={4} wrap="nowrap" align="center" style={{ display: 'inline-flex' }}>
          <span>Last updated</span>
          <Tooltip
            label="This is the date the client's record was last updated. Electronic updates from data feeds like bulk valuations are excluded from this analysis."
            withArrow
            multiline
            w={260}
            color="#1A1A1A"
            styles={{
              tooltip: {
                backgroundColor: '#1A1A1A',
                color: 'var(--mantine-color-white)',
                borderRadius: 15,
                padding: 12,
                fontSize: 'var(--mantine-font-size-xs)',
                fontWeight: FONT_WEIGHTS.medium,
              },
            }}
          >
            <IconInfoCircle
              size={14}
              color="var(--mantine-color-gray-6)"
              style={{ cursor: 'help' }}
            />
          </Tooltip>
        </Group>
      ),
      render: () => (
        <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.6">
          —
        </Text>
      ),
    },
    {
      accessor: 'actions',
      title: '',
      render: (client: Client) => (
        <Box ta="right">
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDotsVertical size={20} color="var(--mantine-color-black)" />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => handleRemovePress(client)}>Remove from family</Menu.Item>
              <Menu.Item color="red" onClick={() => handleDeletePress(client)}>
                Delete client
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Box>
      ),
    },
  ];

  return (
    <>
      <SimpleTable<Client>
        resource="clients"
        filters={{ familyId }}
        pageSize={9999}
        columns={columnsBase}
        variant="new-flow"
        enableSearch={false}
      />
      <ConfirmDeleteModal
        opened={!!clientToDelete}
        onClose={() => setClientToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete client"
        message={`Are you sure you want to delete ${formatName(
          clientToDelete?.firstName,
          clientToDelete?.lastName,
          NameFormat.NATURAL,
        )}? This action cannot be undone.`}
        error={deleteError}
        isLoading={isDeleting}
      />
      <ConfirmDeleteModal
        opened={!!clientToRemove}
        onClose={() => setClientToRemove(null)}
        onConfirm={handleRemoveConfirm}
        title="Remove from family"
        confirmLabel="Remove from family"
        message={`Remove ${formatName(
          clientToRemove?.firstName,
          clientToRemove?.lastName,
          NameFormat.NATURAL,
        )} from ${family?.name ?? 'this family'}? They'll stay a client but will no longer belong to the family.`}
        error={removeError}
        isLoading={isRemoving}
      />
      {confirmationElement}
    </>
  );
}
